import React from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';

import { envIsSweden } from '@/constants/constants';
import { selectShowFluidQuickDeposit, selectUiIsSingleGame } from '@/store/ui/selectors';
import { usePageScrolled } from '@/hooks/usePageScrolled';
import Psa from './Psa';
import AuthButton from '@/components/shared/header/AuthButton';
import classNames from 'classnames';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import { useCashierShow } from '@/features/cashier/useCashier';
import { selectWalletError } from '@/store/wallet/selectors';
import { getUserWallet } from '@/store/wallet/actions';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { ArrowPathIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import TotalBalance from '@/components/TotalBalance';
import Logo from '@/components/shared/logos/Logo';
import InboxBadge from '@/components/shared/header/InboxBadge';
import { useRestrictedMode } from '@/hooks/useRestrictedMode';

interface HeaderProps {
  onNavigate?: (path: string) => boolean;
}

function Header({ onNavigate }: HeaderProps): JSX.Element | null {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const dispatch = useDispatch();
  const isSingleGame = useSelector(selectUiIsSingleGame);
  const showQuickDeposit = useSelector(selectShowFluidQuickDeposit);
  const showCashier = useCashierShow();
  const isRestricted = useRestrictedMode();

  const walletError = useSelector(selectWalletError);

  const isScrolled = usePageScrolled();

  const cashierOnClick = () => {
    if (walletError) {
      dispatch(getUserWallet());
      return;
    }
    triggerGTag(GTagEvents.header_open_cashier);
    showQuickDeposit && !isRestricted
      ? showCashier({ transactionType: 'quick-deposit' })
      : showCashier();
  };

  if (isSingleGame && isMobile && !onNavigate) {
    return null;
  }

  return (
    <header
      id="header"
      className={classNames('lg:pl-[80px] sticky md:fixed z-[3] w-full transition-all', {
        'top-0': !envIsSweden(),
        'top-7': envIsSweden(), // Sweden has Spelpaus header above the main header
      })}
    >
      {!isSingleGame && <Psa />}
      <div className="relative w-full">
        <div className="flex md:bg-transparent bg-white justify-between items-center px-[16px] md:flex-none md:px-0 md:fixed md:right-0">
          <div
            className="md:hidden"
            onClickCapture={e => {
              if (onNavigate && onNavigate('/')) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <Logo />
          </div>
          <div className="relative py-[6px] md:px-[6px]">
            {/* Frosted backing as a sibling layer (not an ancestor of InboxBadge):
                backdrop-filter creates a containing block that would trap the
                Optimove inbox panel rendered inside InboxBadge. Keeping the blur
                off the badge's ancestors lets the panel position against the
                viewport. See InboxBadge.tsx. */}
            <div className="pointer-events-none absolute inset-0 rounded-bl-3xl border-b-[1px] border-l-[1px] border-white/30 backdrop-blur-2xl" />
            <div className="relative flex items-center gap-2">
              <InboxBadge />
              {isAuthenticated && (
                <button
                  type="button"
                  className="flex px-[16px] py-[8px] items-center justify-center rounded-full bg-blue-light font-bold text-white shadow-md transition hover:bg-cyan-600 hover:shadow-lg md:w-auto"
                  onClick={cashierOnClick}
                >
                  {isSingleGame ? (
                    <CreditCardIcon className="h-5 w-5" />
                  ) : (
                    <>
                      <CreditCardIcon className="mr-2 h-5 w-5" />
                      {walletError ? (
                        <ArrowPathIcon strokeWidth={2} height={18} width={18} />
                      ) : (
                        <TotalBalance />
                      )}
                    </>
                  )}
                </button>
              )}

              {!isAuthenticated && <AuthButton isScrolled={isScrolled} />}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
